import classNames from 'classnames'
import { AnimatePresence, Variants, motion } from 'motion/react'
import React, {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { useModal } from '@context/ModalsContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { TooltipDirection } from '@ui/common/layout.types'
import { Button, type ButtonProps } from '@ui/components/Button/Button'
import { TooltipPointerIcon } from '@ui/components/Tooltip/TooltipPointer.icon'

import { useWindowResize } from '@utils/domUtils'
import { getPortalRoot } from '@utils/portals'

import { useScrollListener } from '@hooks/useScrollListener'
import { useTooltipPosition } from '@hooks/useTooltipPosition'

import { InformationModal } from '@shared/modals/Information.modal'

import styles from './Tooltip.module.scss'

type TooltipButtonProps = Pick<
  ButtonProps,
  'onClick' | 'variant' | 'size' | 'disabled' | 'loading'
> & {
  label: string
}

export interface TooltipContentApi {
  close: () => void
}

export type TooltipContent = ReactNode | ((api: TooltipContentApi) => ReactNode)

export interface TooltipProps {
  content?: TooltipContent
  heading?: string
  description?: string
  primaryButton?: TooltipButtonProps
  secondaryButton?: TooltipButtonProps
  pointer?: boolean
  qrCode?: ReactNode
  fullWidth?: boolean
  direction?: TooltipDirection
  autoHide?: number
  disabled?: boolean
  wrapperClassName?: string
  alwaysModal?: boolean
  textAlignCenter?: boolean
  close?: boolean
}

export type UseImperativeHandleRef = { activate: () => void }

const rtlDirectionMap: Record<TooltipDirection, TooltipDirection> = {
  left: 'right',
  right: 'left',
  leftTop: 'rightTop',
  rightTop: 'leftTop',
  leftBottom: 'rightBottom',
  rightBottom: 'leftBottom',
  topLeft: 'topRight',
  topRight: 'topLeft',
  bottomLeft: 'bottomRight',
  bottomRight: 'bottomLeft',
  top: 'top',
  bottom: 'bottom',
}

const variants: Variants = {
  open: {
    opacity: 1,
    transition: {
      delay: 0,
      type: 'spring',
      bounce: 0,
      duration: 0.5,
    },
  },
  closed: {
    opacity: 0,
    transition: {
      type: 'spring',
      bounce: 0,
      duration: 0.25,
    },
  },
}

export const Tooltip = forwardRef<UseImperativeHandleRef, PropsWithChildren<TooltipProps>>(
  (props, ref) => {
    const {
      content,
      heading,
      description,
      primaryButton,
      secondaryButton,
      pointer = true,
      qrCode,
      direction = 'bottom',
      children,
      disabled,
      wrapperClassName,
      fullWidth,
      autoHide,
      alwaysModal,
      textAlignCenter,
      close: forceClose,
    } = props

    const isArabic = useArabicSessionLanguage()

    const hasStructuredContent =
      !!heading || !!description || !!primaryButton || !!secondaryButton || !!qrCode
    const hasContent = !!(content || hasStructuredContent)

    const isMobile = useWindowResize()
    const renderAsModal = !!(isMobile || alwaysModal)

    const triggerRef = useRef<HTMLDivElement>(null)
    const contentWrapperRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const tooltipRootRef = useRef<HTMLElement | null>(null)
    const hideTimeoutRef = useRef<number | null>(null)
    const hoveringTooltipRef = useRef(false)

    const state = useState(false)
    const [active, setActive] = state

    const modalCloseRef = useRef<() => void>(() => {})

    const close = useCallback(() => {
      setActive(false)
      modalCloseRef.current()
    }, [setActive])

    const resolvedContent = typeof content === 'function' ? content({ close }) : content

    const tooltipModal = useModal(InformationModal, {
      description: description ?? resolvedContent,
      title: heading,
      primaryLabel: primaryButton?.label,
      secondaryLabel: secondaryButton?.label,
      onPrimaryClick: (e) => {
        primaryButton?.onClick?.(e)
        tooltipModal.close()
      },
      onSecondaryClick: (e) => {
        secondaryButton?.onClick?.(e)
        tooltipModal.close()
      },
    })

    modalCloseRef.current = tooltipModal.close

    if (!tooltipRootRef.current) {
      tooltipRootRef.current = getPortalRoot('TOOLTIP')
    }

    const { height, width } = contentRef?.current?.getBoundingClientRect() ?? {
      height: 0,
      width: 0,
    }

    const directionForLayout: TooltipDirection = isArabic ? rtlDirectionMap[direction] : direction

    const { contentOrigin, direction: positionedDirection } = useTooltipPosition({
      boxRef: contentWrapperRef,
      contentDirection: directionForLayout,
      isOpen: active,
      triggerRef: triggerRef,
      distance: '10px',
    })

    useHideInteraction(state, renderAsModal)

    const clearHideTimeout = useCallback(() => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        activate() {
          if (!hasContent || disabled) {
            return
          }
          setActive(true)

          if (autoHide) {
            setTimeout(() => setActive(false), autoHide)
          }
        },
      }),
      [hasContent, disabled, setActive, autoHide]
    )

    useEffect(() => {
      return () => {
        clearHideTimeout()
      }
    }, [clearHideTimeout])

    useEffect(() => {
      if (forceClose && active) {
        clearHideTimeout()
        setActive(false)
        modalCloseRef.current()
      }
    }, [forceClose, active, clearHideTimeout, setActive])

    const scheduleHide = () => {
      if (disabled || !hasContent || renderAsModal) {
        return
      }
      clearHideTimeout()
      hideTimeoutRef.current = window.setTimeout(() => {
        if (!hoveringTooltipRef.current) {
          setActive(false)
        }
      }, 75)
    }

    const handleTooltipMouseEnter = () => {
      hoveringTooltipRef.current = true
      clearHideTimeout()
      if (!disabled && hasContent) {
        if (renderAsModal) tooltipModal.open()
        else setActive(true)
      }
    }

    const handleTooltipMouseLeave = () => {
      hoveringTooltipRef.current = false
      scheduleHide()
    }

    const renderStructuredContent = () => {
      const resolvedDescription = description ?? resolvedContent // CHANGED: was `content`

      return (
        <>
          {!!qrCode && <div className={styles.qrWrap}>{qrCode}</div>}
          {heading && <div className={styles.heading}>{heading}</div>}
          {resolvedDescription && <div className={styles.description}>{resolvedDescription}</div>}
          {(primaryButton || secondaryButton) && (
            <div className={styles.actions}>
              {!isArabic && secondaryButton && (
                <Button
                  size={secondaryButton.size ?? 'sm'}
                  variant={secondaryButton.variant ?? 'secondary'}
                  onClick={secondaryButton.onClick}
                  disabled={secondaryButton.disabled}
                  loading={secondaryButton.loading}
                >
                  {secondaryButton.label}
                </Button>
              )}
              {primaryButton && (
                <Button
                  size={primaryButton.size ?? 'sm'}
                  variant={primaryButton.variant ?? 'primary'}
                  onClick={primaryButton.onClick}
                  disabled={primaryButton.disabled}
                  loading={primaryButton.loading}
                >
                  {primaryButton.label}
                </Button>
              )}
              {isArabic && secondaryButton && (
                <Button
                  size={secondaryButton.size ?? 'sm'}
                  variant={secondaryButton.variant ?? 'secondary'}
                  onClick={secondaryButton.onClick}
                  disabled={secondaryButton.disabled}
                  loading={secondaryButton.loading}
                >
                  {secondaryButton.label}
                </Button>
              )}
            </div>
          )}
        </>
      )
    }

    return (
      <div
        onMouseEnter={!isMobile ? handleTooltipMouseEnter : undefined}
        onClickCapture={isMobile ? handleTooltipMouseEnter : undefined}
        onMouseLeave={!isMobile ? handleTooltipMouseLeave : undefined}
        className={classNames(styles.tooltipWrapper, wrapperClassName, {
          [styles.fullWidth]: fullWidth,
        })}
        ref={triggerRef}
      >
        {children}
        {createPortal(
          <AnimatePresence>
            {active && (
              <motion.div
                key='tooltip'
                initial='closed'
                animate='open'
                exit='closed'
                variants={variants}
                className={classNames(styles.tooltip, {
                  [styles.rtl]: isArabic,
                })}
                style={{
                  top: contentOrigin?.top,
                  left: contentOrigin?.left,
                  minHeight: height,
                  minWidth: width,
                  pointerEvents: 'auto',
                }}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
                ref={contentWrapperRef}
              >
                {pointer && (
                  <TooltipPointerIcon
                    positionRules={classNames(styles.pointer, {
                      [styles[`pointer-${positionedDirection}`]]: pointer,
                    })}
                  />
                )}
                <div
                  className={classNames(styles.content, {
                    [styles.textAlignCenter]: textAlignCenter,
                  })}
                  ref={contentRef}
                >
                  {hasStructuredContent ? (
                    renderStructuredContent()
                  ) : (
                    <span>{resolvedContent}</span>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          tooltipRootRef.current!
        )}
      </div>
    )
  }
)

const useHideInteraction = (
  state: [boolean, Dispatch<SetStateAction<boolean>>],
  renderAsModal: boolean
) => {
  const [active, setActive] = state

  const hideOnInteraction = useCallback(() => {
    if (active && renderAsModal) {
      setActive(false)
    }
  }, [active, renderAsModal])

  useScrollListener(hideOnInteraction)

  useEffect(() => {
    if (!active || !renderAsModal) {
      return
    }

    window.addEventListener('scroll', hideOnInteraction, { passive: true })

    const handleTouchMove = () => {
      setActive(false)
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: true })

    return () => {
      window.removeEventListener('scroll', hideOnInteraction)
      document.removeEventListener('touchmove', handleTouchMove)
    }
  }, [active, renderAsModal])
}
